import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';

import useSymptomChecker from 'hooks/useSymptomChecker';
import { useSymptomStore } from 'store/symptom';
import { checkSpanishLocale, cn, extractLabelAndComment } from 'lib/utils';
import { getBodyImage } from 'lib/symptom-checker';
import { Button } from 'components/ui/button';
import { useTranslation } from 'react-i18next';

export default function SymptomBodyPart() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const questionData = useSymptomStore((store) => store.questionData)?.data;

  const isSpanish = checkSpanishLocale();

  const englishChoices = JSON.parse(questionData?.eng_choices || '[]');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '[]');

  const { onSubmit, isLoading } = useSymptomChecker();

  const bodyParts = isSpanish ? spanishChoices : englishChoices;

  const [selectedPartIndex, setSelectedPartIndex] = useState<number>(0);
  const [isOtherLoading, setIsOtherLoading] = useState(false);

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const selected =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];
      let parsedAnswer = [];
      try {
        parsedAnswer = JSON.parse(selected);
      } catch (error) {
        console.log(error);
      }

      const enChoices = JSON.parse(questionData?.eng_choices || '');
      const esChoices = JSON.parse(questionData?.spanish_choices || '');
      const parts = isSpanish ? esChoices : enChoices;
      const bodyIndex = parts.findIndex(
        (option: string) => option === parsedAnswer?.[0]
      );
      setSelectedPartIndex(bodyIndex && bodyIndex >= 0 ? bodyIndex : 0);
    }
  }, [questionData, isSpanish]);

  const onContinue = () => {
    onSubmit({
      eng_choices: JSON.stringify([englishChoices[selectedPartIndex]]),
      spanish_choices: JSON.stringify([spanishChoices[selectedPartIndex]]),
      questionData,
    });
  };

  const onOtherSelect = () => {
    setIsOtherLoading(true);
    onSubmit({
      eng_choices: JSON.stringify(['other']),
      spanish_choices: JSON.stringify(['other']),
      questionData,
    });
  };

  const bodyImage = getBodyImage(englishChoices[selectedPartIndex]);

  const { label } = extractLabelAndComment(
    questionData?.[isSpanish ? 'spanish_question' : 'eng_question'] ?? ''
  );

  return (
    <div className='px-4 pt-4'>
      <div className='grid grid-cols-12 '>
        <div className='col-span-12 md:col-span-8'>
          <div>
            <h2 className='text-base font-semibold md:text-lg'>
              {t('question')} #1
            </h2>

            <div className='mt-3'>
              <p className='mb-4 text-base font-semibold'>{label}</p>
            </div>
          </div>

          <div className='relative block mb-6 md:hidden'>
            <img
              src={bodyImage}
              alt='body-part'
              className='object-contain w-full h-[calc(100vh-22rem)]'
            />
          </div>

          <div className='flex flex-wrap gap-2'>
            {bodyParts.map((part: string, index: number) => {
              const isSelected = selectedPartIndex === index;
              return (
                <motion.div
                  key={part}
                  className={cn(
                    'bg-[#F3F4F6] text-sm font-medium text-[#656D76] px-4 rounded-xl h-8 flex items-center justify-center cursor-pointer transition-all duration-150',
                    isSelected && 'text-white bg-primary'
                  )}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => setSelectedPartIndex(index)}
                >
                  <p className='leading-5'>{part}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
        <div className='relative hidden col-span-4 mt-20 lg:mt-0 md:block'>
          <img
            src={bodyImage}
            alt='body-part'
            className='object-contain w-full h-[calc(100vh-22rem)]'
          />
        </div>
      </div>

      <div className='flex items-center justify-center gap-3 mt-4'>
        <Button
          className='px-6 h-9'
          onClick={onContinue}
          disabled={isLoading && !isOtherLoading}
        >
          {isLoading && !isOtherLoading && (
            <Loader2 className='mr-2 animate-spin size-4' />
          )}
          {code ? t('update') : t('continue')}
        </Button>
        <Button
          variant='outline'
          className='px-6 text-gray-500 border-gray-500 h-9'
          onClick={onOtherSelect}
          disabled={isOtherLoading}
        >
          {isOtherLoading && <Loader2 className='mr-2 animate-spin size-4' />}
          {t('other')}
        </Button>
      </div>
    </div>
  );
}
